import { FC, useContext } from 'react';
import { ConfigContext, ConfigContextType, SMPContainer, SMPFlex } from '@velinsi/smp-widgets-ui';
import { CompetitionsDropdownType, IMultisportCompetition, ISubscriberInfoFn, Languages } from '../../../../../packages/types';
import { MobileModal } from '@velinsi/smp-widgets-ui/src/molecules/MobileModal';
import { CompetitionsDropdown } from '.';
import { defaultHandleUserInteractionClick, getIconAsBgImage, isMobileUserAgent } from '@sportal365/smp-widget-utils';
import { MobileCompetitionsDropdown } from './MobileCompetitionsDropdown';
import MobileDatesDropdown from './MobileDatesDropdown';
import useDetectClickOutside from '../../../../../packages/hooks/useDetectClickOutside';
import { oddsDisplayInMultisport, ProviderLogo } from './ProviderLogo';
import { getMultisportEventsLabels, IMultisportEventsLabels } from '../../utils/getMultisportLabels';
import { BookmakerAssetsType } from '../../widgets';
import { USER_INTERACTION } from '../../../../../packages/constants/entityTypes';
import { DROPDOWN_SELECTION_SUBSCRIBE_TYPE, TAB_SELECTION_SUBSCRIBE_TYPE } from '../../../../../packages/constants/subscribeTypes';

interface MultisportDropdownsProps {
  selectedDate: Date;
  dates: Date[];
  handleDateChange: (option: Date) => void;
  competitions: IMultisportCompetition[];
  selectedCompetition: CompetitionsDropdownType;
  handleCompetitionChange: (option: CompetitionsDropdownType) => void;
  topEventsOption: CompetitionsDropdownType;
  bookmakerAssets?: BookmakerAssetsType;
  localeLang: Languages;
  handleClick: ISubscriberInfoFn;
  showTopEventsAndCompetitionsOptions?: boolean;
}

export const isSameDate = (date1: Date, date2: Date) => {
  return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth() && date1.getDate() === date2.getDate();
};

export const PROVIDER_LOGO_WIDTH = '81px';

export const MultisportDropdowns: FC<MultisportDropdownsProps> = ({
  competitions,
  handleCompetitionChange,
  selectedDate,
  dates,
  handleDateChange,
  topEventsOption,
  selectedCompetition,
  bookmakerAssets,
  localeLang,
  handleClick,
  showTopEventsAndCompetitionsOptions,
}) => {
  const { isComponentVisible, setIsComponentVisible, ref } = useDetectClickOutside<HTMLDivElement>();
  const isMobile = isMobileUserAgent();
  const { dataOptionsDisplay, dataOddsDisplay, dataOddsBettingId, dataBettingLogoDisplay, dataLabels, dataTheme } =
    useContext<ConfigContextType>(ConfigContext);
  const labels = getMultisportEventsLabels(dataLabels as IMultisportEventsLabels);
  const { fullOdds, onlyBettingLogo } = oddsDisplayInMultisport(dataOddsDisplay, dataBettingLogoDisplay, dataOddsBettingId);

  const toggleShowDropdown = () => {
    setIsComponentVisible((prevValue) => !prevValue);
  };

  const handleTabSelectionClick = () => defaultHandleUserInteractionClick(handleClick, USER_INTERACTION, TAB_SELECTION_SUBSCRIBE_TYPE);

  const handleDropdownMenuClick = () => defaultHandleUserInteractionClick(handleClick, USER_INTERACTION, DROPDOWN_SELECTION_SUBSCRIBE_TYPE);

  return (
    <SMPFlex
      ref={ref}
      style={{
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: (fullOdds || onlyBettingLogo) && !dataOptionsDisplay ? '6px' : '20px',
        paddingLeft: '8px',
        paddingRight: '8px',
        paddingBottom: fullOdds || onlyBettingLogo ? '6px' : '16px',
        borderRight: '1px solid var(--colors-borderMultiSportColor)',
      }}
    >
      {dataOptionsDisplay ? (
        <>
          <SMPContainer
            onClick={() => {
              toggleShowDropdown();
              handleDropdownMenuClick();
            }}
            style={{
              margin: 0,
              marginBottom: '4px',
              padding: '7px',
              borderRadius: '6px',
              border: '1px solid var(--colors-gray4)',
              backgroundColor: isComponentVisible ? 'var(--colors-multiSportDropdownButtonBgColor)' : 'var(--colors-transparent)',
              cursor: 'pointer',
            }}
          >
            <SMPContainer
              style={{
                margin: 0,
                padding: 0,
                height: '24px',
                width: '24px',
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                backgroundImage: `url("${getIconAsBgImage(
                  `${isComponentVisible || dataTheme === 'dark' ? 'multisport_dropdown_white' : 'multisport_dropdown_black'}`,
                )}")`,
              }}
            />
          </SMPContainer>
          <CompetitionsDropdown
            dates={dates}
            localeLang={localeLang}
            data={competitions}
            onChange={handleCompetitionChange}
            topEventsOption={topEventsOption}
            isOpen={isComponentVisible && !isMobile}
            toggleDropdown={toggleShowDropdown}
            selectedDate={selectedDate}
            selectedCompetition={selectedCompetition}
            handleDateSelect={handleDateChange}
            labels={labels}
            showTopEventsAndCompetitionsOptions={showTopEventsAndCompetitionsOptions}
            handleTabSelectionClick={handleTabSelectionClick}
          />
          <ProviderLogo bookmakerAssets={bookmakerAssets} handleClick={handleClick} />
          <MobileModal isOpen={isComponentVisible && isMobile} onClose={() => setIsComponentVisible(false)}>
            <SMPFlex
              style={{
                flexDirection: 'column',
                gap: '16px',
                padding: '16px 16px 32px 16px',
                backgroundColor: 'var(--colors-multiSportBgColor)',
              }}
            >
              {showTopEventsAndCompetitionsOptions ? (
                <MobileCompetitionsDropdown
                  options={competitions}
                  selectedCompetition={selectedCompetition}
                  onChange={(data) => {
                    handleCompetitionChange(data);
                  }}
                  closeDropdown={() => setIsComponentVisible(false)}
                  topEventsOption={topEventsOption}
                />
              ) : undefined}

              <MobileDatesDropdown
                options={dates}
                selectedOption={selectedDate}
                handleChange={(data) => {
                  handleDateChange(data);
                  handleTabSelectionClick();
                }}
                closeDropdown={() => setIsComponentVisible(false)}
                localeLang={localeLang}
              />
            </SMPFlex>
          </MobileModal>
        </>
      ) : (
        <ProviderLogo bookmakerAssets={bookmakerAssets} handleClick={handleClick} />
      )}
    </SMPFlex>
  );
};
